import React from 'react';
import { useSettingsStore } from '../../../../../store/settingsStore';
import { fmt } from '../../../../../utils/numberFormat';

interface Props {
  activePowerKw: number;
  reactivePowerKvar: number;
  apparentPowerKva: number;
  capacityKva: number;
  // Stress test mode props
  isStressTestMode?: boolean;
  loadingPercent?: number;
  isOverloaded?: boolean;
}

export const PowerFlowMetrics: React.FC<Props> = ({
  activePowerKw,
  reactivePowerKvar,
  apparentPowerKva,
  capacityKva,
  isStressTestMode = false,
  loadingPercent,
  isOverloaded
}) => {
  const { orange: orangeThreshold, red: redThreshold } = useSettingsStore(s => s.thresholds.transformerLoadLevels);

  // Always use backend-provided loading percent (single source of truth)
  const loadingPercentage = loadingPercent ?? 0;
  // Supply convention (backend TrafoResult.SupplyActivePowerKw / Lemonbeat TotW):
  // positive = the station supplies the LV grid (Bezug from MV), negative = the
  // LV grid feeds back into the MV grid (PV export). Same sign as the houses.
  const isReverseFlow = activePowerKw < -0.05;

  // Header style for stress test mode
  const headerBgClass = isStressTestMode
    ? 'bg-linear-to-r from-orange-50 to-white dark:from-orange-900/20 dark:to-primary-700'
    : 'bg-gray-50 dark:bg-primary-700';
  const iconColorClass = isStressTestMode
    ? 'text-orange-500 dark:text-orange-400'
    : 'text-gray-700 dark:text-white';
  const titleColorClass = isStressTestMode
    ? 'text-orange-900 dark:text-orange-200'
    : 'text-gray-900 dark:text-white';

  return (
    <div className="bg-white dark:bg-primary-800 rounded-md shadow-sm border border-gray-200 dark:border-primary-600 overflow-hidden">
      <div className={`px-3 py-2 ${headerBgClass} border-b border-gray-200 dark:border-primary-600`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-xs font-bold ${titleColorClass} flex items-center`}>
            <svg className={`w-4 h-4 mr-2 ${iconColorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {isStressTestMode ? 'Simulierte Leistung (Stresstest)' : 'Leistungsübersicht'}
          </h3>
          {isStressTestMode && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
              Stresstest
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Power Card */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center space-x-1 mb-0.5">
              <svg className="w-3.5 h-3.5 text-blue-600 dark:text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-xs text-blue-600 dark:text-accent-primary">Wirkleistung</span>
            </div>
            <div className="text-xl font-bold text-blue-700 dark:text-accent-primary leading-tight" title="An das Niederspannungsnetz abgegebene Wirkleistung (Summe der Anschlüsse + Leitungsverluste); negativ = Rückspeisung ins Mittelspannungsnetz">
              {fmt(activePowerKw, 2)} kW
            </div>
            <div className="text-xs text-blue-600 dark:text-accent-primary leading-tight">
              {isReverseFlow ? 'Rückspeisung ins MS-Netz' : 'Bezug aus dem MS-Netz'}
            </div>
          </div>

          {/* Reactive Power Card */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
            <div className="flex items-center space-x-1 mb-0.5">
              <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-xs text-amber-600 dark:text-amber-400">Blindleistung</span>
            </div>
            <div className="text-xl font-bold text-amber-700 dark:text-amber-400 leading-tight">
              {fmt(reactivePowerKvar, 2)} kvar
            </div>
            <div className="text-xs text-amber-600 dark:text-amber-400 leading-tight">
              {reactivePowerKvar < -0.05 ? 'kapazitiv' : reactivePowerKvar > 0.05 ? 'induktiv' : 'neutral'}
            </div>
          </div>

          {/* Apparent Power Card */}
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
            <div className="flex items-center space-x-1 mb-0.5">
              <svg className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-xs text-indigo-600 dark:text-indigo-400">Scheinleistung</span>
            </div>
            <div className="text-xl font-bold text-indigo-700 dark:text-indigo-400 leading-tight">
              {fmt(apparentPowerKva, 2)} kVA
            </div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400 leading-tight">
              {capacityKva > 0 ? `von ${fmt(capacityKva, 0)} kVA Bemessung` : 'Scheinleistung'}
            </div>
          </div>

          {/* Loading Card */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <div className="flex items-center space-x-1 mb-0.5">
              <svg className="w-3.5 h-3.5 text-green-600 dark:text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-green-600 dark:text-success">Auslastung</span>
            </div>
            <div className="text-xl font-bold text-green-700 dark:text-success leading-tight">
              {fmt(loadingPercentage, 1)} %
            </div>
            <div className="text-xs text-green-600 dark:text-success leading-tight" title="Scheinleistung bezogen auf die Bemessungsscheinleistung des Transformators">S / S<sub>n</sub></div>
          </div>
        </div>

        {/* Status Summary */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isOverloaded || loadingPercentage >= redThreshold ? 'bg-red-500 dark:bg-error animate-pulse' :
              loadingPercentage >= orangeThreshold ? 'bg-orange-500 dark:bg-warning' :
              'bg-green-500 dark:bg-success'
            }`}></div>
            <span className={`${
              isOverloaded || loadingPercentage >= redThreshold
                ? 'text-red-600 dark:text-error font-medium'
                : 'text-gray-600 dark:text-primary-300'
            }`}>
              {isOverloaded || loadingPercentage >= redThreshold ? 'Kritische Auslastung!' :
               loadingPercentage >= orangeThreshold ? 'Hohe Auslastung' :
               'Status: Normal'}
            </span>
          </div>
          {isStressTestMode && (
            <span className="text-orange-500 dark:text-orange-400">
              Simulation
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
